import React, { useEffect, useMemo, useState } from 'react';
import { CheckCircleIcon, ExclamationTriangleIcon, MegaphoneIcon, PaperAirplaneIcon } from '@heroicons/react/24/outline';
import { autoplannerAPI, usersAPI } from '../../../services/api';
import useValidationFlow from '../../../hooks/autoplanner/useValidationFlow';

const Step3AvailabilitySimple = ({
    trainingGroupId,
    selectedSessions,
    startDate,
    endDate,
    taskId,
    onComplete,
    onBack,
    registerNav,
}) => {
    const [sendingAll, setSendingAll] = useState(false);
    const [sendingMap, setSendingMap] = useState({}); // { [userId]: bool }
    const [userInfoById, setUserInfoById] = useState({}); // { [userId]: userInfo }
    const [perDayResolutions, setPerDayResolutions] = useState({}); // { [userId]: { [date]: resolution } }

    const {
        validationData,
        isProcessing,
        progress,
        currentStep,
        validateNow,
    } = useValidationFlow({
        taskId,
        trainingGroupId,
        selectedSessions,
        startDate,
        endDate,
    });

    const availabilityCheck = validationData?.availability_check;
    const missingUsers = useMemo(() => {
        const availability = availabilityCheck || {};
        const missingSet = new Set();
        const userFromMissing = new Map();

        // Add backend-reported missing rowers
        (availability.missing_users || []).forEach((u) => {
            if (!u || !u.user_id) return;
            missingSet.add(u.user_id);
            userFromMissing.set(u.user_id, u);
        });

        // Build coach id set to exclude coaches from rower list
        const coachIds = new Set([
            ...((availability.missing_coaches || []).map((c) => c.user_id)),
            ...((availability.coaches_with_availability || []).map((c) => c.user_id)),
        ]);

        // Include users with 'ignore' resolutions (auto-ignored → still show as unavailable here)
        Object.entries(perDayResolutions || {}).forEach(([userIdStr, userResolutions]) => {
            const userId = parseInt(userIdStr, 10);
            if (!userId || coachIds.has(userId)) return;
            const hasIgnored = Object.values(userResolutions || {}).some((resolution) => resolution === 'ignore');
            if (hasIgnored) {
                missingSet.add(userId);
                if (!userFromMissing.has(userId)) {
                    const ignoredDates = Object.entries(userResolutions || {})
                        .filter(([, res]) => res === 'ignore')
                        .map(([date]) => date);
                    userFromMissing.set(userId, { user_id: userId, missing_dates: ignoredDates });
                }
            }
        });

        // Enrich with known rower details if present
        const knownRowers = new Map([
            ...((availability.rowers_with_availability || []).map((u) => [u.user_id, u])),
            ...((availability.missing_users || []).map((u) => [u.user_id, u])),
        ]);

        return Array.from(missingSet).map((id) => userFromMissing.get(id) || knownRowers.get(id) || { user_id: id });
    }, [availabilityCheck, perDayResolutions]);
    const isComplete = useMemo(() => (missingUsers?.length || 0) === 0, [missingUsers]);

    useEffect(() => {
        if (trainingGroupId && selectedSessions && startDate && endDate) {
            if (!validationData) validateNow();
        }
    }, [trainingGroupId, selectedSessions, startDate, endDate, validationData, validateNow]);

    // Hydrate per-day resolutions so simple step reflects auto-ignored rowers
    useEffect(() => {
        if (!taskId) return;
        (async () => {
            try {
                const result = await autoplannerAPI.getResolutions(taskId);
                if (result && result.success) {
                    setPerDayResolutions(result.resolutions || {});
                }
            } catch (e) {
                // non-blocking
            }
        })();
    }, [taskId, validationData]);

    // Prefill name/phone details map from availability data
    useEffect(() => {
        const availability = availabilityCheck || {};
        const next = {};
        try {
            (availability.missing_users || []).forEach((u) => {
                if (u && u.user_id && !userInfoById[u.user_id]) next[u.user_id] = u;
            });
            (availability.rowers_with_availability || []).forEach((u) => {
                if (u && u.user_id && !userInfoById[u.user_id]) next[u.user_id] = u;
            });
        } catch {
            // ignore
        }
        if (Object.keys(next).length > 0) {
            setUserInfoById((prev) => ({ ...prev, ...next }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [availabilityCheck]);

    // Fetch missing user details when needed to display names
    useEffect(() => {
        const idsToFetch = (missingUsers || [])
            .map((u) => u?.user_id)
            .filter((id) => {
                if (!id) return false;
                const known = userInfoById[id];
                const hasName = Boolean(known?.name || known?.first_name || known?.last_name);
                const inList = (missingUsers || []).find((x) => x?.user_id === id) || {};
                const listHasName = Boolean(inList?.name || inList?.first_name || inList?.last_name);
                return !hasName && !listHasName;
            });
        if (!idsToFetch || idsToFetch.length === 0) return;
        (async () => {
            try {
                const results = await Promise.all(
                    idsToFetch.map((id) => usersAPI.getUser(id).catch(() => null))
                );
                const fetched = {};
                results.forEach((r) => {
                    if (!r) return;
                    const key = r.user_id || r.id;
                    if (key) fetched[key] = r;
                });
                if (Object.keys(fetched).length > 0) {
                    setUserInfoById((prev) => ({ ...prev, ...fetched }));
                }
            } catch {
                // ignore
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [missingUsers]);

    useEffect(() => {
        if (!registerNav) return;
        registerNav({
            onNext: async () => {
                if (!isComplete) return;
                try {
                    if (taskId) {
                        await autoplannerAPI.createNormalizedDataset(taskId);
                    }
                } catch (e) {
                    // non-blocking
                    // eslint-disable-next-line no-console
                    console.warn('Failed to create normalized dataset (non-blocking):', e);
                }
                onComplete({}, {});
            },
            onBack: onBack,
            canNext: !isProcessing && isComplete,
            canBack: !isProcessing,
        });
    }, [registerNav, isProcessing, isComplete, onBack, onComplete, taskId]);

    const resolveUser = (user) => {
        if (!user || !user.user_id) return user;
        const extra = userInfoById[user.user_id] || {};
        return { ...extra, ...user };
    };

    const getDisplayName = (user) => {
        const u = resolveUser(user);
        const name = u?.name || [u?.first_name, u?.last_name].filter(Boolean).join(' ').trim();
        return name || `User ${u?.user_id || u?.id || ''}`.trim();
    };

    const getFirstName = (user) => {
        const u = resolveUser(user);
        if (u?.first_name) return u.first_name;
        const name = u?.name || '';
        return name.split(' ')[0] || 'Rower';
    };

    const getIntlPhone = (user) => {
        const u = resolveUser(user);
        const cc = u?.country_code || '';
        const pn = u?.phone_number || u?.phone || '';
        const digits = `${cc}${pn}`.replace(/[^\d]/g, '');
        return digits || null;
    };

    const encodeMessage = (text) => encodeURIComponent(text);

    const buildWhatsAppUrlForUser = (user) => {
        const firstName = getFirstName(user);
        const message = `Hoi ${firstName}, wil je je beschikbaarheid invullen in de planner? Alvast bedankt!`;
        const encoded = encodeMessage(message);
        const phone = getIntlPhone(user);
        if (phone) return `https://wa.me/${phone}?text=${encoded}`;
        return `https://wa.me/?text=${encoded}`;
    };

    const buildWhatsAppUrlForAll = () => {
        const list = missingUsers.map(getDisplayName).join(', ');
        const message = `Deze roeiers moeten hun beschikbaarheid nog invullen: ${list}`;
        const encoded = encodeMessage(message);
        return `https://wa.me/?text=${encoded}`;
    };

    const handleSendReminder = async (userId) => {
        if (!taskId || !userId) return;
        setSendingMap((m) => ({ ...m, [userId]: true }));
        try {
            await autoplannerAPI.sendAvailabilityReminder(taskId, userId);
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error('Failed to send reminder', e);
        } finally {
            setSendingMap((m) => ({ ...m, [userId]: false }));
        }
    };

    const handleSendAllReminders = async () => {
        if (!taskId || missingUsers.length === 0) return;
        setSendingAll(true);
        try {
            await Promise.all(
                missingUsers.map((u) => autoplannerAPI.sendAvailabilityReminder(taskId, u.user_id))
            );
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error('Failed to send some reminders', e);
        } finally {
            setSendingAll(false);
        }
    };

    if (isProcessing) {
        return (
            <div className="space-y-6">
                <div className="text-center">
                    <div className="mx-auto h-12 w-12 animate-spin rounded-full border-b-2 border-blue-600"></div>
                    <h2 className="mt-4 text-lg font-medium text-gray-900">Validating Availability</h2>
                    <p className="mt-2 text-sm text-gray-600">{currentStep}</p>
                </div>
                <div className="rounded-lg bg-white p-6 shadow">
                    <div className="space-y-4">
                        <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-gray-700">Progress</span>
                            <span className="text-sm text-gray-500">{progress}%</span>
                        </div>
                        <div className="h-2 w-full rounded-full bg-gray-200">
                            <div className="h-2 rounded-full bg-blue-600 transition-all duration-300" style={{ width: `${progress}%` }}></div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (!validationData) {
        return (
            <div className="space-y-6 pb-24 sm:pb-0">
                <div className="rounded-lg bg-white p-6 shadow">
                    <div className="text-center">
                        <p className="mb-4 text-sm text-gray-600">Klik op de knop hieronder om de beschikbaarheid te controleren.</p>
                        <button
                            onClick={validateNow}
                            className="inline-flex items-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                        >
                            Start Validatie
                        </button>
                    </div>
                </div>
                <div className="flex justify-between">
                    <button
                        type="button"
                        onClick={onBack}
                        className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                    >
                        Back
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6 pb-24 sm:pb-0">
            <div className="rounded-lg bg-white p-6 shadow">
                <div className="mb-3 flex items-center gap-2">
                    {isComplete ? (
                        <CheckCircleIcon className="h-5 w-5 text-green-500" />
                    ) : (
                        <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500" />
                    )}
                    <h3 className="text-lg font-medium text-gray-900">
                        Rower Availability: {isComplete ? 'Complete' : 'Incomplete'}
                    </h3>
                </div>

                {!isComplete && (
                    <div className="space-y-4">
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                            <div className="text-sm text-gray-700">
                                {missingUsers.length} rower(s) missing availability
                            </div>
                            <div className="flex flex-wrap gap-2">
                                <button
                                    onClick={handleSendAllReminders}
                                    disabled={sendingAll || missingUsers.length === 0}
                                    className="inline-flex items-center rounded bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
                                    title="Send in-app notifications to all missing rowers"
                                >
                                    <MegaphoneIcon className="mr-2 h-4 w-4" /> Notify All
                                </button>
                                <a
                                    href={buildWhatsAppUrlForAll()}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center rounded bg-green-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-700"
                                    title="Open WhatsApp with a pre-filled message for all missing rowers"
                                >
                                    <PaperAirplaneIcon className="mr-2 h-4 w-4" /> WhatsApp All
                                </a>
                            </div>
                        </div>

                        <div className="divide-y divide-gray-100 rounded border">
                            {missingUsers.map((user) => (
                                <div key={user.user_id} className="flex flex-col gap-2 p-3 sm:flex-row sm:items-center sm:justify-between">
                                    <div className="min-w-0">
                                        <div className="text-sm font-medium text-gray-900">{getDisplayName(user)}</div>
                                        {user?.missing_dates?.length > 0 && (
                                            <div className="text-xs text-gray-600">Missing {user.missing_dates.length} date(s)</div>
                                        )}
                                    </div>
                                    <div className="flex flex-wrap gap-2 sm:ml-2">
                                        <button
                                            onClick={() => handleSendReminder(user.user_id)}
                                            disabled={!!sendingMap[user.user_id]}
                                            className="rounded border border-indigo-300 bg-white px-2.5 py-1 text-sm text-indigo-700 hover:bg-indigo-50 disabled:opacity-50"
                                            title="Send in-app notification"
                                        >
                                            Notify
                                        </button>
                                        <a
                                            href={buildWhatsAppUrlForUser(user)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="rounded bg-green-600 px-2.5 py-1 text-sm text-white hover:bg-green-700"
                                            title="Open WhatsApp with pre-filled message"
                                        >
                                            WhatsApp
                                        </a>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {isComplete && (
                    <div className="flex items-center gap-2 text-green-700">
                        <CheckCircleIcon className="h-5 w-5 text-green-500" />
                        <span className="text-sm">Alle roeiers hebben hun beschikbaarheid ingevuld. Je kunt doorgaan.</span>
                    </div>
                )}
            </div>

            <div className="flex items-center justify-between">
                <button
                    type="button"
                    onClick={onBack}
                    className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                    Back
                </button>
                <button
                    type="button"
                    onClick={async () => {
                        if (!isComplete) return;
                        try {
                            if (taskId) await autoplannerAPI.createNormalizedDataset(taskId);
                        } catch (e) {
                            // noop
                        }
                        onComplete({}, {});
                    }}
                    disabled={!isComplete}
                    className="inline-flex items-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
                >
                    Continue
                </button>
            </div>
        </div>
    );
};

export default Step3AvailabilitySimple;


